// ---------------------------------------------------------------
// timeclockService.js — Fichaje de jornada.
//
// Colección `timeclock`: un documento por persona y día con id
// `${uid}_${YYYY-MM-DD}`:
//   { userId, date, month: "YYYY-MM", updatedAt, ultimaCorreccion?,
//     entries: [{ in: "HH:MM:SS", inMs, out: "HH:MM:SS"|null, outMs,
//                 turno, autoClosed? }] }
//
// LA HORA: `inMs` / `outMs` son milisegundos que el SERVIDOR comprueba
// (las reglas rechazan un marcaje a más de 2 minutos de su hora). Los
// textos "HH:MM:SS" los escribe el móvil y no se pueden validar, así
// que para MOSTRAR se recalculan desde los milisegundos
// (`normalizarEntradas`). Para ESCRIBIR, en cambio, hay que devolver
// cada marcaje tal cual estaba guardado: si se "arreglara" su texto,
// las reglas lo verían como un retoque de un marcaje antiguo y
// rechazarían la escritura entera.
//
// Un marcaje que corrige la dirección pierde su `inMs`/`outMs` (ya no
// es una hora comprobada) y la corrección queda en `timeclockHistorial`.
//
// Documento `timeclockConfig/main` (lo edita el admin, lo leen
// todos): { allowedIPs: ["x.x.x.x", …] } — la red de la farmacia.
// Las incidencias de cada persona viven en `incidencias/{uid}`.
//
// Colección `devices/{uid}`: dispositivo personal vinculado
// { deviceId, username, boundAt }. Se crea UNA vez por el propio
// usuario; sólo el administrador puede restablecerlo.
// ---------------------------------------------------------------
import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { toISO } from "../utils/date";

const pad = (n) => String(n).padStart(2, "0");

/** Hora actual exacta "HH:MM:SS" */
export const nowTime = () => {
  const d = new Date();
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const clockDocId = (uid, dateISO) => `${uid}_${dateISO}`;

// La hora de la farmacia, se mire desde donde se mire
const fmtHora = new Intl.DateTimeFormat("es-ES", {
  timeZone: "Europe/Madrid",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

/** Marcajes para MOSTRAR: la hora sale de lo que comprobó el servidor */
export function normalizarEntradas(entries = []) {
  return (entries || []).map((e) => ({
    ...e,
    in: typeof e.inMs === "number" ? fmtHora.format(e.inMs) : e.in,
    out: typeof e.outMs === "number" ? fmtHora.format(e.outMs) : e.out,
  }));
}
const paraMostrar = (data) =>
  data ? { ...data, entries: normalizarEntradas(data.entries) } : data;

/** Firestore no admite `undefined`: fuera las claves vacías */
const limpia = (e) =>
  Object.fromEntries(Object.entries(e).filter(([, v]) => v !== undefined));

export const AUTO_CLOSE_GRACE_MIN = 15; // margen tras el fin del turno
const FALLBACK_MAX_HOURS = 12; // sin horario asignado: jornada máxima

const toSec = (t) => {
  const [h, m, sec] = t.split(":").map(Number);
  return h * 3600 + m * 60 + (sec || 0);
};

export const secToHMS = (sec) => {
  const s = ((sec % 86400) + 86400) % 86400;
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
};

/** Fin del turno asignado ese día en segundos (la guardia cruza la
 *  medianoche → puede superar 86400). null si no hay turno. */
export function scheduledEndSecFromDays(scheduleDays, dateISO) {
  const info = scheduleDays?.[String(Number(dateISO.slice(8, 10)))];
  if (!info?.shifts?.length || info.baja || info.ownVacation) return null;
  let end = 0;
  for (const sh of info.shifts) {
    const st = toSec(sh.start);
    let e = toSec(sh.end);
    if (e <= st) e += 86400; // nocturno (guardia)
    end = Math.max(end, e);
  }
  return end;
}

async function scheduledEndSec(uid, dateISO) {
  const snap = await getDoc(doc(db, "schedules", `${uid}_${dateISO.slice(0, 7)}`));
  if (!snap.exists()) return null;
  return scheduledEndSecFromDays(snap.data().days, dateISO);
}

/** Momento (ms) en el que un fichaje abierto debe autocerrarse */
export function autoCloseAtMs(dateISO, inTime, schedEndSec) {
  const endSec =
    schedEndSec !== null && schedEndSec !== undefined
      ? schedEndSec
      : toSec(inTime) + FALLBACK_MAX_HOURS * 3600;
  const closeSec = endSec + AUTO_CLOSE_GRACE_MIN * 60;
  const base = new Date(dateISO + "T00:00:00");
  return { atMs: base.getTime() + closeSec * 1000, outTime: secToHMS(closeSec) };
}

/** Si la jornada abierta superó su límite, la cierra automáticamente
 *  (out = fin de turno + margen) marcándola como autoClosed. */
async function maybeAutoClose(uid, dateISO, data, idx) {
  const entry = data.entries[idx];
  const schedEnd = await scheduledEndSec(uid, dateISO);
  const { atMs, outTime } = autoCloseAtMs(dateISO, entry.in, schedEnd);
  if (Date.now() < atMs) return false;
  const entries = data.entries.map((e, i) =>
    i === idx ? { ...e, out: outTime, outMs: atMs, autoClosed: true } : e
  );
  try {
    await writeDay(uid, dateISO, entries);
  } catch (err) {
    // Una entrada antigua sin hora comprobada no se puede cerrar sola
    // desde el móvil (las reglas no tienen con qué acotarla). Se deja
    // abierta: la cierra la dirección, o la persona fichando la salida.
    console.warn("No se pudo cerrar sola la jornada:", err);
    return false;
  }
  return true;
}

async function readDay(uid, dateISO) {
  const snap = await getDoc(doc(db, "timeclock", clockDocId(uid, dateISO)));
  return snap.exists() ? snap.data() : null;
}

// Con `merge`: un día que la dirección ya corrigió lleva su
// `ultimaCorreccion`, y una escritura completa la borraría; las reglas
// sólo dejan al empleado tocar `entries` y `updatedAt`.
async function writeDay(uid, dateISO, entries) {
  await setDoc(
    doc(db, "timeclock", clockDocId(uid, dateISO)),
    {
      userId: uid,
      date: dateISO,
      month: dateISO.slice(0, 7),
      entries: entries.map(limpia),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/** Busca la jornada ABIERTA (sin salida) de hoy o de ayer (guardias) */
export async function findOpenEntry(uid) {
  const today = toISO(new Date());
  const yesterday = toISO(new Date(Date.now() - 24 * 3600 * 1000));
  for (const dateISO of [today, yesterday]) {
    const data = await readDay(uid, dateISO);
    const idx = (data?.entries || []).findIndex((e) => !e.out);
    if (data && idx !== -1) {
      // ¿Se olvidó de fichar la salida? → autocierre al límite
      const closed = await maybeAutoClose(uid, dateISO, data, idx);
      if (closed) continue;
      return {
        dateISO,
        index: idx,
        // Para mostrar (la hora comprobada)…
        entry: normalizarEntradas([data.entries[idx]])[0],
        // …y para escribir, tal cual está guardado
        entries: data.entries,
      };
    }
  }
  return null;
}

/** Fichar ENTRADA ahora mismo */
export async function punchIn(uid, turno) {
  const open = await findOpenEntry(uid);
  if (open) {
    throw Object.assign(new Error(), { code: "app/already-open" });
  }
  const dateISO = toISO(new Date());
  const data = await readDay(uid, dateISO);
  const entries = [
    ...(data?.entries || []),
    { in: nowTime(), inMs: Date.now(), out: null, turno: turno || null },
  ];
  await writeDay(uid, dateISO, entries);
  return { dateISO, in: entries[entries.length - 1].in };
}

/** Fichar SALIDA ahora mismo (cierra la jornada abierta) */
export async function punchOut(uid) {
  const open = await findOpenEntry(uid);
  if (!open) {
    throw Object.assign(new Error(), { code: "app/not-open" });
  }
  const entries = open.entries.map((e, i) =>
    i === open.index ? { ...e, out: nowTime(), outMs: Date.now() } : e
  );
  await writeDay(uid, open.dateISO, entries);
  return { dateISO: open.dateISO, out: entries[open.index].out };
}

/**
 * La dirección corrige las entradas/salidas de un día concreto.
 *
 * Deja SIEMPRE rastro: en el mismo lote se crea un apunte en
 * `timeclockHistorial` con lo que había, lo que queda, quién y por qué,
 * y las reglas rechazan la corrección si falta. Antes se sobrescribía
 * la hora original sin más.
 *
 * Un marcaje cuya hora se cambia pierde su `inMs`/`outMs`: deja de ser
 * una hora comprobada por el servidor y pasa a ser una corrección.
 *
 * @param motivo obligatorio (mínimo 3 caracteres, lo exigen las reglas)
 * @param porNombre nombre de quien corrige, para leerlo sin buscarlo
 */
export async function adminSaveDay(
  uid,
  dateISO,
  entries,
  { motivo, porNombre, solicitudId } = {}
) {
  if (!motivo || motivo.trim().length < 3) {
    throw Object.assign(new Error("Falta el motivo de la corrección"), {
      code: "app/sin-motivo",
    });
  }
  const antes = (await readDay(uid, dateISO))?.entries || [];
  // El editor trabaja con las horas TAL COMO SE MUESTRAN (las del
  // servidor), así que hay que comparar con esa misma versión: contra el
  // texto crudo, un marcaje intacto parecería cambiado por un segundo.
  const mostrados = normalizarEntradas(antes);
  const hms = (t) => (t && t.length === 5 ? `${t}:00` : t || null);

  const despues = entries.map((e, i) => {
    const previo = antes[i];
    const visto = mostrados[i];
    if (!previo) {
      // Tramo nuevo añadido por la dirección: no es una hora comprobada
      const { inMs, outMs, ...resto } = e;
      return limpia({ ...resto, in: hms(e.in), out: hms(e.out) });
    }
    const mismaEntrada = hms(e.in) === hms(visto.in);
    const mismaSalida = hms(e.out) === hms(visto.out);
    // Lo que no ha cambiado se deja EXACTAMENTE como estaba guardado
    // (con su hora comprobada); lo que cambia pierde la comprobación.
    const nuevo = {
      ...previo,
      in: mismaEntrada ? previo.in : hms(e.in),
      inMs: mismaEntrada ? previo.inMs : undefined,
      out: mismaSalida ? previo.out : hms(e.out),
      outMs: mismaSalida ? previo.outMs : undefined,
      // Si se corrige la salida de un cierre automático, ya no es
      // automática: es la hora buena, y deja de contar como incidencia.
      // Sólo sigue marcada cuando la salida la pone el propio cierre.
      autoClosed: mismaSalida
        ? previo.autoClosed
        : e.autoClosed && !previo.out
        ? true
        : undefined,
    };
    return limpia(nuevo);
  });

  const historial = doc(collection(db, "timeclockHistorial"));
  const batch = writeBatch(db);
  batch.set(historial, {
    userId: uid,
    date: dateISO,
    antes: antes.map(limpia),
    despues,
    por: auth.currentUser?.uid,
    porNombre: porNombre || "",
    motivo: motivo.trim().slice(0, 300),
    at: serverTimestamp(),
  });
  batch.set(
    doc(db, "timeclock", clockDocId(uid, dateISO)),
    {
      userId: uid,
      date: dateISO,
      month: dateISO.slice(0, 7),
      entries: despues,
      updatedAt: serverTimestamp(),
      ultimaCorreccion: historial.id,
    },
    { merge: true }
  );
  // Si la corrección responde a una petición de la persona, la
  // petición se da por aprobada en el MISMO lote: las reglas no dejan
  // aprobarla sin la corrección y su historial.
  if (solicitudId) {
    batch.update(doc(db, "timeclockSolicitudes", solicitudId), {
      estado: "aprobada",
      resueltaPor: auth.currentUser?.uid,
      resueltaPorNombre: porNombre || "",
      resueltaAt: serverTimestamp(),
      historialId: historial.id,
    });
  }
  await batch.commit();
}

/* ---------------- Correcciones que pide la persona ---------------
   Quien olvida fichar ya no tiene que decírselo a la dirección de
   palabra: propone las horas buenas y el porqué, y la dirección la
   aprueba (pasa por `adminSaveDay`, con su historial) o la rechaza.
   Colección `timeclockSolicitudes`:
     { userId, userName, date, propuesta: [{ in, out }], motivo,
       estado: "pendiente" | "aprobada" | "rechazada" | "retirada",
       creadaAt, resueltaPor?, resueltaPorNombre?, resueltaAt?,
       respuesta?, historialId? }
   Ninguna se borra (las reglas no dejan): son la constancia de que la
   corrección la pidió la propia persona.
   --------------------------------------------------------------- */
/** Paso de "pide la corrección de hoy" desde el Inicio al fichaje
 *  (sessionStorage: dura lo que la pestaña) */
export const PEDIR_CORRECCION_KEY = "fichaje-pedir-correccion";

const hmsDe = (t) => (t && t.length === 5 ? `${t}:00` : t || null);

/** La persona pide corregir un día suyo */
export function pedirCorreccion(uid, { userName, date, propuesta, motivo }) {
  return addDoc(collection(db, "timeclockSolicitudes"), {
    userId: uid,
    userName: (userName || "").slice(0, 60),
    date,
    propuesta: (propuesta || [])
      .slice(0, 6)
      .map((e) => ({ in: hmsDe(e.in), out: hmsDe(e.out) })),
    motivo: motivo.trim().slice(0, 300),
    estado: "pendiente",
    creadaAt: serverTimestamp(),
  });
}

const masRecientes = (lista) =>
  lista.sort((a, b) => (b.creadaAt?.seconds || 0) - (a.creadaAt?.seconds || 0));

/** Las solicitudes de una persona (las ve ella) */
export function subscribeMisSolicitudes(uid, onData, onError) {
  return onSnapshot(
    query(collection(db, "timeclockSolicitudes"), where("userId", "==", uid)),
    (snap) => onData(masRecientes(snap.docs.map((d) => ({ id: d.id, ...d.data() })))),
    onError
  );
}

/** Las pendientes de todo el equipo (sólo dirección) */
export function subscribeSolicitudesPendientes(onData, onError) {
  return onSnapshot(
    query(collection(db, "timeclockSolicitudes"), where("estado", "==", "pendiente")),
    (snap) => onData(masRecientes(snap.docs.map((d) => ({ id: d.id, ...d.data() })))),
    onError
  );
}

/** La dirección la rechaza (la aprobación va por `adminSaveDay`) */
export function rechazarSolicitud(id, { respuesta, porNombre } = {}) {
  return updateDoc(doc(db, "timeclockSolicitudes", id), {
    estado: "rechazada",
    resueltaPor: auth.currentUser?.uid,
    resueltaPorNombre: porNombre || "",
    resueltaAt: serverTimestamp(),
    respuesta: (respuesta || "").trim().slice(0, 300),
  });
}

/** La persona la retira mientras siga pendiente */
export function retirarSolicitud(id) {
  return updateDoc(doc(db, "timeclockSolicitudes", id), {
    estado: "retirada",
    resueltaAt: serverTimestamp(),
  });
}

/** Correcciones de los fichajes de una persona (las ve ella misma) */
export function subscribeCorrecciones(uid, onData, onError) {
  return onSnapshot(
    query(collection(db, "timeclockHistorial"), where("userId", "==", uid)),
    (snap) =>
      onData(
        snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.at?.seconds || 0) - (a.at?.seconds || 0))
      ),
    onError
  );
}

/** Fichajes de HOY de un usuario: un solo documento en vez de la
 *  consulta del mes entero (mucho más ligero para los avisos) */
export function subscribeUserDay(uid, dateISO, onData, onError) {
  return onSnapshot(
    doc(db, "timeclock", clockDocId(uid, dateISO)),
    (snap) => onData(snap.exists() ? { [dateISO]: paraMostrar(snap.data()) } : {}),
    onError
  );
}

/** Fichajes de un usuario en un mes ("YYYY-MM"), en tiempo real */
export function subscribeUserMonth(uid, monthKeyStr, onData, onError) {
  const q = query(
    collection(db, "timeclock"),
    where("userId", "==", uid),
    where("month", "==", monthKeyStr)
  );
  return onSnapshot(
    q,
    (snap) => {
      const map = {};
      snap.docs.forEach((d) => (map[d.data().date] = paraMostrar(d.data())));
      onData(map);
    },
    onError
  );
}

/** Fichajes de TODO el equipo en un mes (sólo admin) */
export function subscribeAllMonth(monthKeyStr, onData, onError) {
  const q = query(
    collection(db, "timeclock"),
    where("month", "==", monthKeyStr)
  );
  return onSnapshot(
    q,
    (snap) => onData(snap.docs.map((d) => paraMostrar(d.data()))),
    onError
  );
}

/* ---------------- Configuración (red de la farmacia) ------------ */
export function subscribeTimeclockConfig(onData, onError) {
  return onSnapshot(
    doc(db, "timeclockConfig", "main"),
    (snap) => onData(snap.exists() ? snap.data() : null),
    onError
  );
}

/* ---------------- Incidencias de cada persona ------------------
   Antes vivían en `timeclockConfig/main`, que lee todo el equipo: las
   incidencias descartadas de cada uno y las explicaciones de la
   dirección (que pueden llevar datos de salud) estaban al alcance de
   cualquiera. Ahora cada persona tiene su documento `incidencias/{uid}`
   y las reglas sólo dejan leerlo a ella y a la dirección.
   La clave de una incidencia empieza por el uid ("uid|fecha|tipo|…").
   --------------------------------------------------------------- */
const uidDeClave = (clave) => String(clave).split("|")[0];

/** Las incidencias de UNA persona: { descartadas: [], notas: [] } */
export function subscribeIncidencias(uid, onData, onError) {
  return onSnapshot(
    doc(db, "incidencias", uid),
    (snap) => onData(snap.exists() ? snap.data() : { descartadas: [], notas: [] }),
    onError
  );
}

/** Las de TODO el equipo, por uid (sólo dirección) */
export function subscribeTodasIncidencias(onData, onError) {
  return onSnapshot(
    collection(db, "incidencias"),
    (snap) => {
      const map = {};
      snap.docs.forEach((d) => (map[d.id] = d.data()));
      onData(map);
    },
    onError
  );
}

/** El admin descarta ("borra") una incidencia concreta para siempre */
export function dismissIncidentKey(key) {
  return setDoc(
    doc(db, "incidencias", uidDeClave(key)),
    { descartadas: arrayUnion(key), updatedAt: serverTimestamp() },
    { merge: true }
  );
}

/**
 * Guarda (o borra) la explicación de una incidencia.
 *
 * Se almacena como LISTA y no como mapa con la clave dentro: la clave
 * de una incidencia lleva su texto de detalle, y un mapa de Firestore
 * con claves libres se complica en cuanto aparece un punto. Con una
 * lista da igual lo que ponga la clave.
 *
 * @param notas lista completa ya modificada (un solo admin a la vez,
 *   así que no hace falta nada más fino)
 */
export function saveIncidentNotes(uid, notas) {
  return setDoc(
    doc(db, "incidencias", uid),
    { notas, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

/**
 * Lleva las incidencias que aún estén en la configuración general a su
 * sitio nuevo, una por persona, y las borra de allí. Se ejecuta sola
 * cuando la dirección abre el fichaje; si no queda nada, no hace nada.
 */
export async function migrarIncidencias(config) {
  const viejas = config?.dismissedIncidents || [];
  const notas = config?.incidentNotes || [];
  if (!viejas.length && !notas.length) return 0;

  const porUid = {};
  const de = (uid) => (porUid[uid] = porUid[uid] || { descartadas: [], notas: [] });
  viejas.forEach((k) => de(uidDeClave(k)).descartadas.push(k));
  notas.forEach((n) => de(uidDeClave(n.key)).notas.push(n));

  const batch = writeBatch(db);
  for (const [uid, d] of Object.entries(porUid)) {
    const ref = doc(db, "incidencias", uid);
    const actual = await getDoc(ref);
    const yaNotas = actual.exists() ? actual.data().notas || [] : [];
    const claves = new Set(yaNotas.map((n) => n.key));
    batch.set(
      ref,
      {
        ...(d.descartadas.length ? { descartadas: arrayUnion(...d.descartadas) } : {}),
        notas: [...yaNotas, ...d.notas.filter((n) => !claves.has(n.key))],
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  }
  batch.set(
    doc(db, "timeclockConfig", "main"),
    { dismissedIncidents: deleteField(), incidentNotes: deleteField() },
    { merge: true }
  );
  await batch.commit();
  return viejas.length + notas.length;
}

export function saveTimeclockConfig(data) {
  return setDoc(
    doc(db, "timeclockConfig", "main"),
    { ...data, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

/** IP pública actual del dispositivo */
export async function fetchMyIP() {
  const res = await fetch("https://api.ipify.org?format=json");
  const json = await res.json();
  return json.ip;
}

/* ---------------- Dispositivo personal vinculado ---------------- */
const DEVICE_ID_KEY = "fichaje-device-id";
const DEVICE_UID_KEY = "fichaje-bound-uid";

/** Identificador de ESTE navegador (se crea una vez y queda en caché) */
export function localDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

export function localBoundUid() {
  return localStorage.getItem(DEVICE_UID_KEY) || null;
}

/** Dispositivo registrado del usuario en Firebase (null si no tiene) */
export async function getRegisteredDevice(uid) {
  const snap = await getDoc(doc(db, "devices", uid));
  return snap.exists() ? snap.data() : null;
}

/**
 * Vincula ESTE dispositivo al usuario (una sola vez).
 * Falla si este navegador ya está vinculado a otra persona.
 */
export async function bindThisDevice(uid, username) {
  const boundTo = localBoundUid();
  if (boundTo && boundTo !== uid) {
    throw Object.assign(new Error(), { code: "app/device-other-user" });
  }
  const deviceId = localDeviceId();
  await setDoc(doc(db, "devices", uid), {
    deviceId,
    username: username || "",
    boundAt: serverTimestamp(),
  });
  localStorage.setItem(DEVICE_UID_KEY, uid);
  return deviceId;
}

/** El admin desvincula el dispositivo de un usuario (podrá vincular otro) */
export function adminResetDevice(uid) {
  return deleteDoc(doc(db, "devices", uid));
}

/* ---------------- Consentimiento del fichaje -------------------- */
// v2: se detalló qué hace y qué NO hace la comprobación de
// dispositivo. Al subir la versión, quien aceptó la v1 la vuelve a
// leer: así el justificante que se descarga siempre coincide con el
// texto que esa persona tuvo delante.
// v3: la hora la comprueba el servidor, las correcciones dejan rastro y
// se ven, nadie borra fichajes, y se dice que la comprobación de red la
// hace el navegador y que el código del dispositivo también se copia al
// servidor (revisión de protección de datos, noviembre de 2026).
export const CONSENT_VERSION = 3;

/** ¿Ha aceptado este usuario los términos del fichaje? (tiempo real) */
export function subscribeConsent(uid, onData, onError) {
  return onSnapshot(
    doc(db, "timeclockConsent", uid),
    (snap) => onData(snap.exists() ? snap.data() : null),
    onError
  );
}

/** Lectura puntual del consentimiento (para los avisos) */
export async function getConsentOnce(uid) {
  const snap = await getDoc(doc(db, "timeclockConsent", uid));
  return snap.exists() ? snap.data() : null;
}

/** Registra en Firebase que el usuario acepta los términos */
export function acceptConsent(uid, username) {
  return setDoc(doc(db, "timeclockConsent", uid), {
    accepted: true,
    version: CONSENT_VERSION,
    username: username || "",
    acceptedAt: serverTimestamp(),
  });
}
